// Which new attention items reach the top-right stack, and in what order —
// BL-19. The stack shows three; this decides which three matter.
//
// ═══ THREE LEVELS ═══
//
//   SOS      — never enters this stack. It has its own pinned lane above it,
//              uncounted, and leaves only on acknowledgement (BL-18/BL-19:
//              "SOS is exempt from the limit"). The toast library has no notion
//              of priority, so the only way to be immune to its limit is to be
//              outside it.
//   urgent   — `danger` tone: payment, no-show, lost connection, late.
//   heads-up — `warning` tone: no rider, cash waiting.
//
// Two rules, both so a heads-up can never push an urgent alert out of view:
//   1. Within one batch, heads-ups are shown FIRST and urgent ones LAST. The
//      stack shows the newest on top, so urgent lands on top.
//   2. When urgent alerts already fill the stack, a new heads-up is not shown
//      at all. It is not lost — the Attention tab still lists it.

/**
 * Anything that can go in the stack: a live-map alert, or a bell notification
 * (0278) mapped onto the same two tones.
 */
export interface StackItem {
  tone: "danger" | "warning";
}

export const ALERT_STACK_LIMIT = 3;

/** One toast in the stack with its gap — a three-line card is about this tall. */
export const ALERT_SLOT_PX = 104;

/**
 * How many toasts fit between the top of the stack and the top of the map's
 * controls (Leon's audit, 2026-10-23: three toasts under two SOS cards ran
 * over zoom and recenter). Never more than the limit, never fewer than one —
 * an alert that does not fit is still in the Attention tab.
 */
export function alertsThatFit(stackTop: number, controlsTop: number): number {
  const room = Math.floor((controlsTop - stackTop) / ALERT_SLOT_PX);
  return Math.max(1, Math.min(ALERT_STACK_LIMIT, room));
}

export const URGENT_ALERT_MS = 20_000;
export const HEADS_UP_ALERT_MS = 10_000;

export function isUrgent(item: StackItem): boolean {
  return item.tone === "danger";
}

/**
 * How many urgent alerts each toaster currently has on screen.
 *
 * ⚠ SHARED ACROSS SOURCES ON PURPOSE, AND THAT IS THE BL-19 FIX. Two hooks feed
 * the same stack — the map's own attention items (`useLiveMapAlerts`) and the
 * bell's notifications (`useNotificationToasts`) — and on /live-map they feed
 * the SAME toaster. Each used to count only the urgent toasts IT had raised, so
 * a heads-up notification arriving while three urgent map alerts were up saw a
 * count of nought, showed itself, and pushed one of them out of a three-slot
 * stack. The rule said a heads-up can never bury an urgent alert; with two
 * private counters it only held within one source.
 *
 * Module-level rather than React state: the toasts outlive the renders that
 * raised them, and a re-render must not reset what is on screen.
 */
const urgentOnScreen = new Map<string, Set<string>>();

function slot(toasterId: string): Set<string> {
  const existing = urgentOnScreen.get(toasterId);
  if (existing) return existing;
  const created = new Set<string>();
  urgentOnScreen.set(toasterId, created);
  return created;
}

/** How many urgent alerts are showing on this toaster, from every source. */
export function urgentShowing(toasterId: string): number {
  return slot(toasterId).size;
}

/** Called as an urgent toast is raised. */
export function noteUrgentShown(toasterId: string, toastId: string): void {
  slot(toasterId).add(toastId);
}

/** Called when one leaves the screen — dismissed, auto-closed or cleared. */
export function noteUrgentGone(toasterId: string, toastId: string): void {
  slot(toasterId).delete(toastId);
}

/**
 * Forget a whole toaster's alerts. The live map's toaster unmounts with the
 * page, taking its toasts with it; without this the count would keep counting
 * alerts that are no longer on screen and silence every later heads-up.
 */
export function clearUrgentShowing(toasterId: string): void {
  urgentOnScreen.delete(toasterId);
}

/**
 * The order to show `fresh` in, given how many urgent alerts are already up.
 * Pure: the hook owns the toasts, this owns the decision.
 */
export function planAlertStack<T extends StackItem>(
  fresh: readonly T[],
  urgentShowing: number,
): T[] {
  const urgent = fresh.filter(isUrgent);
  const headsUp = fresh.filter((item) => !isUrgent(item));
  const room = ALERT_STACK_LIMIT - urgentShowing - urgent.length;
  // The last `room` heads-ups are the newest of them; older ones would sink
  // under the urgent ones anyway.
  const shownHeadsUp = room > 0 ? headsUp.slice(-room) : [];
  return [...shownHeadsUp, ...urgent];
}
